import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  RefreshCcw, Loader2, Check, X, AlertCircle, ArrowLeft,
  DollarSign, Clock, User, FileText, ChevronDown, ChevronUp
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminRefundsPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [refundRequests, setRefundRequests] = useState([]);
  const [stats, setStats] = useState(null);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [actionType, setActionType] = useState(null); // 'approve' or 'reject'
  const [approvedAmount, setApprovedAmount] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [filter, setFilter] = useState('pending');
  const [expandedId, setExpandedId] = useState(null);

  const isAdmin = user?.email === 'oanjorin@anjolc.com' || user?.is_admin;

  useEffect(() => {
    if (!authLoading && (!isAuthenticated || !isAdmin)) {
      navigate('/');
    }
  }, [authLoading, isAuthenticated, isAdmin, navigate]);

  useEffect(() => {
    if (isAuthenticated && isAdmin) {
      fetchRefundRequests();
    }
  }, [isAuthenticated, isAdmin, filter]);

  const getAuthHeaders = () => {
    const sessionToken = localStorage.getItem('session_token');
    return sessionToken ? { 'Authorization': `Bearer ${sessionToken}` } : {};
  };

  const fetchRefundRequests = async () => {
    try {
      setLoading(true);
      const headers = getAuthHeaders();
      
      const statusParam = filter !== 'all' ? `?status=${filter}` : '';
      const res = await fetch(`${API_URL}/api/admin/refunds${statusParam}`, {
        headers,
        credentials: 'include'
      });
      
      if (res.ok) {
        const data = await res.json();
        setRefundRequests(data.refund_requests || []);
        setStats(data.stats || null);
      }
    } catch (error) {
      console.error('Failed to fetch refund requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!selectedRequest || !approvedAmount) return;
    
    const amountCents = Math.round(parseFloat(approvedAmount) * 100);
    if (isNaN(amountCents) || amountCents <= 0) {
      setMessage({ type: 'error', text: 'Please enter a valid amount' });
      return;
    }
    
    setSubmitting(true);
    setMessage({ type: '', text: '' });
    
    try {
      const headers = {
        ...getAuthHeaders(),
        'Content-Type': 'application/json'
      };
      
      const res = await fetch(`${API_URL}/api/admin/refunds/${selectedRequest.id}/approve`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({
          approved_refund_cents: amountCents,
          admin_notes: adminNotes.trim() || null
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        setMessage({ 
          type: 'success', 
          text: data.stripe_error 
            ? `Approved but Stripe error: ${data.stripe_error}` 
            : `Refund approved! ${data.approved_amount_display} refunded.`
        });
        closeModal();
        fetchRefundRequests();
      } else {
        const error = await res.json();
        setMessage({ type: 'error', text: error.detail || 'Failed to approve refund' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to approve refund' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!selectedRequest || !adminNotes.trim()) {
      setMessage({ type: 'error', text: 'Please provide a reason for rejection' });
      return;
    }
    
    setSubmitting(true);
    setMessage({ type: '', text: '' });
    
    try {
      const headers = {
        ...getAuthHeaders(),
        'Content-Type': 'application/json'
      };
      
      const res = await fetch(`${API_URL}/api/admin/refunds/${selectedRequest.id}/reject`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({
          admin_notes: adminNotes.trim()
        })
      });
      
      if (res.ok) {
        setMessage({ type: 'success', text: 'Refund request rejected' });
        closeModal();
        fetchRefundRequests();
      } else {
        const error = await res.json();
        setMessage({ type: 'error', text: error.detail || 'Failed to reject refund' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to reject refund' });
    } finally {
      setSubmitting(false);
    }
  };

  const openActionModal = (request, type) => {
    setSelectedRequest(request);
    setActionType(type);
    setApprovedAmount(type === 'approve' ? (request.requested_refund_cents / 100).toFixed(2) : '');
    setAdminNotes('');
  };

  const closeModal = () => {
    setSelectedRequest(null);
    setActionType(null);
    setApprovedAmount('');
    setAdminNotes('');
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      approved: 'bg-green-500/20 text-green-400 border-green-500/30',
      rejected: 'bg-red-500/20 text-red-400 border-red-500/30'
    };
    
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full border ${styles[status] || styles.pending}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-[#050810]">
        <Header />
        <div className="flex items-center justify-center py-40">
          <Loader2 className="h-8 w-8 text-[#3B82F6] animate-spin" />
          <span className="ml-3 text-gray-400">Loading refund requests...</span>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050810]">
      <Header />
      
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate('/admin')}
          className="text-gray-400 hover:text-white mb-6"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Admin Panel
        </Button>

        {/* Page Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-[#F97316]/20 rounded-xl">
              <RefreshCcw className="h-6 w-6 text-[#F97316]" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">Refund Management</h1>
              <p className="text-gray-400 text-sm">Review and process refund requests</p>
            </div>
          </div>
          <Button
            onClick={fetchRefundRequests}
            variant="outline"
            className="border-[#1e2a45] text-gray-300"
          >
            <RefreshCcw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Message */}
        {message.text && (
          <div className={`mb-6 p-4 rounded-xl border flex items-center gap-3 ${
            message.type === 'success' 
              ? 'bg-green-500/10 border-green-500/30 text-green-400'
              : 'bg-red-500/10 border-red-500/30 text-red-400'
          }`}>
            {message.type === 'success' ? <Check className="h-5 w-5" /> : <AlertCircle className="h-5 w-5" />}
            {message.text}
          </div>
        )}

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-[#131b2e] rounded-xl border border-[#1e2a45] p-4">
              <div className="flex items-center gap-2 text-yellow-400 text-sm mb-1">
                <Clock className="h-4 w-4" />
                Pending
              </div>
              <span className="text-2xl font-bold text-white">{stats.pending}</span>
            </div>
            <div className="bg-[#131b2e] rounded-xl border border-[#1e2a45] p-4">
              <div className="flex items-center gap-2 text-green-400 text-sm mb-1">
                <Check className="h-4 w-4" />
                Approved
              </div>
              <span className="text-2xl font-bold text-white">{stats.approved}</span>
            </div>
            <div className="bg-[#131b2e] rounded-xl border border-[#1e2a45] p-4">
              <div className="flex items-center gap-2 text-red-400 text-sm mb-1">
                <X className="h-4 w-4" />
                Rejected
              </div>
              <span className="text-2xl font-bold text-white">{stats.rejected}</span>
            </div>
            <div className="bg-[#131b2e] rounded-xl border border-[#1e2a45] p-4">
              <div className="flex items-center gap-2 text-[#3B82F6] text-sm mb-1">
                <DollarSign className="h-4 w-4" />
                Total Refunded
              </div>
              <span className="text-2xl font-bold text-white">{stats.total_refunded_display}</span>
            </div>
          </div>
        )}

        {/* Filter Tabs */}
        <div className="flex gap-2 mb-6">
          {['pending', 'approved', 'rejected', 'all'].map((f) => (
            <Button
              key={f}
              variant={filter === f ? 'default' : 'ghost'}
              onClick={() => setFilter(f)}
              className={filter === f 
                ? 'bg-[#3B82F6] text-white' 
                : 'text-gray-400 hover:text-white'}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
              {f === 'pending' && stats?.pending > 0 && (
                <span className="ml-2 px-2 py-0.5 bg-yellow-500/20 text-yellow-400 text-xs rounded-full">
                  {stats.pending}
                </span>
              )}
            </Button>
          ))}
        </div>

        {/* Refund Requests List */}
        <div className="bg-[#131b2e] rounded-2xl border border-[#1e2a45] overflow-hidden">
          {refundRequests.length === 0 ? (
            <div className="p-8 text-center">
              <FileText className="h-12 w-12 text-gray-600 mx-auto mb-3" />
              <p className="text-gray-400">No {filter !== 'all' ? filter : ''} refund requests</p>
            </div>
          ) : (
            <div className="divide-y divide-[#1e2a45]">
              {refundRequests.map((req) => (
                <div 
                  key={req.id}
                  className="p-4 hover:bg-[#1e2a45]/30 transition-colors"
                  data-testid={`refund-${req.id}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <User className="h-4 w-4 text-gray-500" />
                        <span className="text-white font-medium">{req.user_email}</span>
                        {getStatusBadge(req.status)}
                      </div>
                      <div className="flex items-center gap-4 text-sm text-gray-400 mb-2">
                        <span>
                          Original: ${req.original_amount_cents / 100}
                        </span>
                        <span>
                          Requested: <span className="text-[#F97316]">${req.requested_refund_cents / 100}</span>
                        </span>
                        {req.approved_refund_cents && (
                          <span>
                            Approved: <span className="text-green-400">${req.approved_refund_cents / 100}</span>
                          </span>
                        )}
                        <span className="text-gray-500">
                          {new Date(req.created_at).toLocaleDateString()}
                        </span>
                      </div>
                      
                      {/* Expandable Details */}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setExpandedId(expandedId === req.id ? null : req.id)}
                        className="text-gray-400 hover:text-white p-0 h-auto"
                      >
                        {expandedId === req.id ? (
                          <>
                            <ChevronUp className="h-4 w-4 mr-1" />
                            Hide Details
                          </>
                        ) : (
                          <>
                            <ChevronDown className="h-4 w-4 mr-1" />
                            Show Details
                          </>
                        )}
                      </Button>
                      
                      {expandedId === req.id && (
                        <div className="mt-3 p-3 bg-[#1e2a45]/50 rounded-lg text-sm">
                          <p className="text-gray-400 mb-2">
                            <strong className="text-gray-300">Reason:</strong> {req.reason}
                          </p>
                          <p className="text-gray-400 mb-2">
                            <strong className="text-gray-300">Tier:</strong> {req.tier}
                          </p>
                          <p className="text-gray-400 mb-2">
                            <strong className="text-gray-300">Period:</strong>{' '}
                            {new Date(req.period_start).toLocaleDateString()} - {new Date(req.period_end).toLocaleDateString()}
                          </p>
                          {req.admin_notes && (
                            <p className="text-gray-400">
                              <strong className="text-gray-300">Admin Notes:</strong> {req.admin_notes}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                    
                    {req.status === 'pending' && (
                      <div className="flex gap-2 ml-4">
                        <Button
                          size="sm"
                          onClick={() => openActionModal(req, 'approve')}
                          className="bg-green-600 hover:bg-green-700 text-white"
                          data-testid={`approve-btn-${req.id}`}
                        >
                          <Check className="h-4 w-4 mr-1" />
                          Approve
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openActionModal(req, 'reject')}
                          className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                          data-testid={`reject-btn-${req.id}`}
                        >
                          <X className="h-4 w-4 mr-1" />
                          Reject
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <Footer />

      {/* Action Modal */}
      <Dialog open={!!selectedRequest && !!actionType} onOpenChange={closeModal}>
        <DialogContent className="sm:max-w-md bg-[#050810] border-[#1e2a45]">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-white flex items-center gap-2">
              {actionType === 'approve' ? (
                <>
                  <Check className="h-5 w-5 text-green-400" />
                  Approve Refund
                </>
              ) : (
                <>
                  <X className="h-5 w-5 text-red-400" />
                  Reject Refund
                </>
              )}
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              {actionType === 'approve' 
                ? 'Confirm the refund amount and process via Stripe'
                : 'Provide a reason for rejecting this refund request'}
            </DialogDescription>
          </DialogHeader>
          
          {selectedRequest && (
            <div className="space-y-4 mt-4">
              {/* Request Summary */}
              <div className="bg-[#1e2a45]/50 rounded-xl p-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">User</span>
                    <p className="text-white">{selectedRequest.user_email}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Original Amount</span>
                    <p className="text-white">${selectedRequest.original_amount_cents / 100}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Requested Refund</span>
                    <p className="text-[#F97316]">${selectedRequest.requested_refund_cents / 100}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Tier</span>
                    <p className="text-white">{selectedRequest.tier}</p>
                  </div>
                </div>
              </div>

              {/* Approve: Amount Input */}
              {actionType === 'approve' && (
                <div>
                  <label className="text-gray-300 text-sm mb-2 block">
                    Approved Refund Amount ($) <span className="text-red-400">*</span>
                  </label>
                  <Input
                    type="number"
                    step="0.01"
                    value={approvedAmount}
                    onChange={(e) => setApprovedAmount(e.target.value)}
                    placeholder="0.00"
                    className="bg-[#1e2a45] border-[#2d3a56] text-white"
                    data-testid="approved-amount-input"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Max: ${selectedRequest.original_amount_cents / 100}
                  </p>
                </div>
              )}

              {/* Admin Notes */}
              <div>
                <label className="text-gray-300 text-sm mb-2 block">
                  Admin Notes {actionType === 'reject' && <span className="text-red-400">*</span>}
                </label>
                <Textarea
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  placeholder={actionType === 'reject' 
                    ? "Reason for rejection (required)..."
                    : "Optional notes..."}
                  className="bg-[#1e2a45] border-[#2d3a56] text-white min-h-[80px]"
                  data-testid="admin-notes-input"
                />
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4">
                <Button
                  variant="outline"
                  onClick={closeModal}
                  className="border-[#1e2a45] text-gray-300"
                >
                  Cancel
                </Button>
                {actionType === 'approve' ? (
                  <Button
                    onClick={handleApprove}
                    disabled={submitting || !approvedAmount}
                    className="bg-green-600 hover:bg-green-700 text-white"
                    data-testid="confirm-approve-btn"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <Check className="h-4 w-4 mr-2" />
                        Approve & Refund
                      </>
                    )}
                  </Button>
                ) : (
                  <Button
                    onClick={handleReject}
                    disabled={submitting || !adminNotes.trim()}
                    className="bg-red-600 hover:bg-red-700 text-white"
                    data-testid="confirm-reject-btn"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <X className="h-4 w-4 mr-2" />
                        Reject Request
                      </>
                    )}
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminRefundsPage;
